import React, {createRef, useState, useContext, useEffect, useRef} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import __ from "lodash";
import moment from "moment";
import axios from "axios";
import {v4 as uuidv4} from "uuid";
import {Grid, Icon, useTheme} from "@material-ui/core";
import {DoNotDisturb} from "@mui/icons-material";
import {
  CalendarToday, DateRange, Event,
  FiberNew,
  Help, InsertInvitation, MailOutlined, SaveAlt,
  Snooze, Today,
  UnsubscribeOutlined,
} from "@material-ui/icons";
import {OpenAiIcon} from "../../../icons";
import MaterialTable from "@material-table/core";
import {validateEmail} from "../../../functions/EmailValidation";
import SystemContext from "../../../SystemContext";
import useDimensions from "../../../blocks/useDimensions";
import {ColumnWidth} from "../../../blocks/ColumnHelper";
import StringToJSX from "../../../functions/StringToJsx";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import {defaultComponentsScroll, MaterialTableBody, MaterialTableSummaryRow, FullWidthFilter} from "../../../controls/Material-Table-Components"
import {IconButtonMenu, LightTooltip} from "../../../controls";
import {nextActionDateTime} from "./EnquiryHelperFunctions";
import {useEmailMessages, useUpdateEmailMessage} from "../../../services/emailMessages";
import {useAddEnquiry} from "../../../services/enquiry";
import AddEnquiryDialog from "./AddEnquiryDialog";


const apiSitePrefix = getApiSitePrefix();
const AccessControl = require ("../../../AccessControl");

const AddressToolTip = (props) => {
  return (
    <Grid container nowrap="true" key={"address-container"}>
      <Grid item xs={12} nowrap="true" style={{ minWidth: 800 }}><div style={{ display: "inline-block", minWidth: 45, fontWeight: "800", verticalAlign: "top" }}>Sender: </div><div style={{ display: "inline-block", width: 755, textOverflow: "ellipsis" }}>{props.sender}</div></Grid>
      <Grid item xs={12}/>
      <Grid item xs={12} nowrap="true" style={{ minWidth: 800 }}><div style={{ display: "inline-block", minWidth: 45, fontWeight: "800", verticalAlign: "top" }}>To: </div><div style={{ display: "inline-block", width: 755, textOverflow: "ellipsis" }}>{props.toAddress}</div></Grid>
      <Grid item xs={12}/>
      <Grid item xs={12} nowrap="true" style={{ minWidth: 800 }}><div style={{ display: "inline-block", minWidth: 45, fontWeight: "800", verticalAlign: "top" }}>CC: </div><div style={{ display: "inline-block", width: 755, textOverflow: "ellipsis" }}>{props.ccAddress}</div></Grid>
    </Grid>
  );
}

const queryOpenAi = async (SystemData, prompt, sender) => {

  let message = "";
  try {
    SystemData.toggleLoadingProgress(true, "Using AI to analyse email message ...");
    const query = {
      prompt: prompt,
      sender: sender,
    }
    const response = await axios.post(`${apiSitePrefix}/openai/query`, {query: query});
    SystemData.toggleLoadingProgress(false);
    if (response.data && response.data.success) {
      return response.data.aiResponse;
    }

    if (!response.data) {
      message = `Could not get OpenAI response. Server is not responding`;
      console.log(message);
      SystemData.openAlert("Error", message);
    } else if (!response.data.success) {
      message = `Could not get OpenAI response. Server returned ${response.data.error}`;
      console.log(message);
      SystemData.openAlert("Error" , message);
    }
  } catch (err) {
    SystemData.toggleLoadingProgress(false);
    message = `Could not get OpenAI response. Server is not responding`;
    console.log(message);
    SystemData.openAlert("Error", message);
  }

  return null;
}


const EnquiryMessageList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();

  const props = {
    ..._props
  }
  const {classes, costingClasses} = props;

  const userInfo = AccessControl.getUserInfo();
  const userClass = userInfo?.userClass;
  const defaults = {};

  const [state, setState] = useState({
    activeRow: null,
    selectedIndex: null,
    canEdit: props.canEdit||false,
    direction: "IN"
  });

  let selectedRow = -1;

  const SystemData = useContext(SystemContext);
  const eventBus = SystemData.eventBus;

  const handleAddEnquiry = useAddEnquiry();
  const handleUpdateMessage = useUpdateEmailMessage();

  const tableRef = createRef({state: {lastEditingRow: false}});
  const queryClient = useQueryClient();
  let extraKeys = [];
  const emailMessages = useEmailMessages({tableRef, queryClient, extraKeys, useCache: false});


  const handleCloseAddEnquiry = () => {
    queryClient.invalidateQueries(["EmailMessages"]);
  }

  const analyseRoute = (result, cargoObject) => {
    if (__.has(result, "route")) {
      if (typeof result.route === "string") {
        const routeParts = result.route.toUpperCase().split(/[^A-Za-z]/);
        if (routeParts.length > 1) {
          routeParts.forEach(part => {
            if (part !== "") {
              if (__.isEmpty(cargoObject))
                cargoObject["originAirportCode"] = part;
              else
                cargoObject["destinationAirportCode"] = part;
            }
          });
        }
      } else if (typeof result.route === "object" && !__.isEmpty(result.route)) {
        if (__.has(result.route, "origin")) {
          cargoObject["originAirportCode"] = result.route.origin;
        }
        if (__.has(result.route, "originAirportCode")) {
          cargoObject["originAirportCode"] = result.route.originAirportCode;
        }
        if (__.has(result.route, "originAirport")) {
          const originAirportParts = result.route.originAirport.split(",");
          if (originAirportParts.length > 1) {
            cargoObject["originAirportCode"] = originAirportParts[0];
          } else {
            cargoObject["originAirportCode"] = result.route.originAirport;
          }
        }
        if (__.has(result.route, "departureAirportCode")) {
          cargoObject["originAirportCode"] = result.route.departureAirportCode;
        }
        if (__.has(result.route, "departureAirport")) {
          const departureAirportParts = result.route.departureAirport.split(",");
          if (departureAirportParts.length > 1) {
            cargoObject["originAirportCode"] = departureAirportParts[0];
          } else {
            cargoObject["originAirportCode"] = result.route.departureAirport;
          }
        }
        if (__.has(result.route, "departurePoint")) {
          cargoObject["originAirportCode"] = result.route.departurePoint;
        }
        if (__.has(result.route, "departurePoints")) {
          cargoObject["originAirportCode"] = result.route.departurePoints;
        }

        if (__.has(result.route, "destination")) {
          cargoObject["destinationAirportCode"] = result.route.destination;
        }
        if (__.has(result.route, "destinationAirportCode")) {
          cargoObject["destinationAirportCode"] = result.route.destinationAirportCode;
        }
        if (__.has(result.route, "destinationAirport")) {
          const destinationAirportParts = result.route.destinationAirport.split(",");
          if (destinationAirportParts.length > 1) {
            cargoObject["destinationAirportCode"] = destinationAirportParts[0];
          } else {
            cargoObject["destinationAirportCode"] = result.route.destinationAirport;
          }
        }
        if (__.has(result.route, "arrivalAirportCode")) {
          cargoObject["destinationAirportCode"] = result.route.arrivalAirportCode;
        }
        if (__.has(result.route, "arrivalAirport")) {
          const arrivalAirportParts = result.route.arrivalAirport.split(",");
          if (arrivalAirportParts.length > 1) {
            cargoObject["destinationAirportCode"] = arrivalAirportParts[0];
          } else {
            cargoObject["destinationAirportCode"] = result.route.arrivalAirport;
          }
        }
        if (__.has(result.route, "arrivalPoint")) {
          cargoObject["destinationAirportCode"] = result.route.arrivalPoint;
        }
      }
    }
  }

  const analysePayload = (result, cargoObject) => {
    if (__.has(result, "payload")) {
      let payloadUnitised = false;
      const resultString = JSON.stringify(result).toUpperCase();
      if (resultString.includes("UNITISED") || resultString.includes("UNITIZED") || resultString.includes("UNITS") || resultString.includes("BUP")) {
        payloadUnitised = 1;
      }

      if (typeof result.payload === "string") {
        if (result.payload.toUpperCase().includes("MAX")) {
          //TODO: this is hardwired to 747F
          cargoObject["payloadLoose"] = 100000;
        } else {
          const payloadParts = result.payload.replaceAll(/[\.\,]/g, "").split(/[^0-9]/);
          const payloadUnitParts = result.payload.replaceAll(/[\.\,0-9]/g, "").split(/[^A-Za-z]/);
          if (payloadParts.length > 0) {
            payloadParts.forEach(part => {
              if (!isNaN(Number(part)) && Number(part) > 0) {
                if (!__.isEmpty(cargoObject)) {
                  if (__.intersection(payloadUnitParts, ["t", "T", "ton", "TON", "tons", "TONS", "tonnes", "TONNES"]).length > 0)
                    cargoObject["payload"] = Number(part) * 1000;
                  else
                    cargoObject["payload"] = Number(part);
                }
                cargoObject["payloadLoose"] = !payloadUnitised ? cargoObject["payload"] : 0;
                cargoObject["payloadUnitised"] = payloadUnitised ? cargoObject["payload"] : 0;
                cargoObject["bupCount"] = 0;
              }
            });
          }
        }
      } else if (!isNaN(Number(result.payload))) {
        cargoObject["payloadLoose"] = Number(result.payload);
      }
    }
  }

  const analyseDimensions = (result, cargoObject) => {
    if (__.has(result, "commodity"))
      cargoObject["notes"] = result.commodity;
    if (__.has(result, "dimensions")) {
      if (typeof result.dimensions === "string")
        cargoObject["notes"] = result.commodity.trim() + "; " + result.dimensions;
      else if (result.dimensions && typeof result.dimensions === "object") {
        const  dimensions = Object.keys(result.dimensions);
        dimensions.forEach(dimension => {
          if (typeof result.dimensions[dimension] === "string")
            cargoObject["notes"] += "; " + __.startCase(dimension) + ": " + result.dimensions[dimension];
          else if (typeof result.dimensions[dimension] === "object") {
            cargoObject["notes"] += ";\n" + __.startCase(dimension) + ": " +  JSON.stringify(result.dimensions[dimension]);
          }
        });
      }
    }
  }

  const analyseEmail = async (SystemData, emailBody, sender) => {

    const prompt = `Please extract email sender company name, email sender name, email sender address, email sender email, email sender phone, commodity, dimensions, route, payload or weight as payload, enquiry date, enquiry time and the enquiry or request or rfq or rfp details from the following email. If you find weight return it as Payload. If pallets are present return it as unitised. If BUP are present return it as BUP. If you find date expressed in calendar weeks or expressed as CW convert that date to the Monday of that calendar week and return it as enquiryDate. If there is no explicit route look for two or more IATA airport codes. Please return results as JSON using camel case: ${emailBody}`;
    try {
      const response = await queryOpenAi(SystemData, prompt, sender);
      if (response)
        return response
    } catch (error) {
      console.error(error.response.status, error.response.data);
    }
    return {};
  }

  const processEmailAnalysisResult = (result, rowData) => {

    if (typeof result === "string") {
      result = JSON.parse(result);
    }

    const cargo = [];
    if (__.has(result, "routes") && Array.isArray(result.routes)) {
      result["route"] = result.routes;
    }
    if (__.has(result, "route")) {
      if (!Array.isArray(result.route)) {
        result.route = [result.route];
      }
      if (Array.isArray(result.route)) {
        result.route.forEach(route => {
          if (typeof route !== "string" && typeof route === "object" && !__.isEmpty(route) && Array.isArray(Object.entries(route)[0][1])) {
            const objectEntries = Object.entries(route);
            const origins = objectEntries[0][1];
            if (Array.isArray(origins)) {
              origins.forEach(origin => {
                const cargoObject = {};
                const routeFromOrigin = {}
                routeFromOrigin[objectEntries[0][0]] = origin;
                for (let i = 1; i < objectEntries.length; i++) {
                  const key = objectEntries[i][0];
                  const value = objectEntries[i][1];
                  routeFromOrigin[key] = value;
                }
                analyseRoute({route: routeFromOrigin}, cargoObject);
                analysePayload(result, cargoObject);
                analyseDimensions(result, cargoObject);

                if (!__.isEmpty(cargoObject)) {
                  cargo.push(cargoObject);
                }
              });
            }
          } else {
            const cargoObject = {};
            analyseRoute({route}, cargoObject);
            analysePayload(result, cargoObject);
            analyseDimensions(result, cargoObject);

            if (!__.isEmpty(cargoObject)) {
              cargo.push(cargoObject);
            }
          }
        });
      }
    }

    if (cargo.length > 0 && __.has(cargo[0], "originAirportCode")) {
      // Save the data so next time there is no need to call openAI
      rowData["extractedContent"] = result;
      new Promise((resolve, reject) => {
        handleUpdateMessage.mutate({record: rowData, oldRecord: rowData, resolve, reject, extraKeys});
      }).then(()=>{queryClient.invalidateQueries(["EmailMessages"])}).catch((error) => {console.log("it's bad") /* TODO: create an alert */});
    }

    let enquiryDate = null;
    if(__.has(result, "enquiryDate")) {
      enquiryDate = moment(result.enquiryDate);
    }

    if (__.has(result, "emailSenderPhone")) {
      if (typeof result.emailSenderPhone === "string") {
        result.emailSenderPhone = result.emailSenderPhone.replaceAll(/[\s()-]/g, "");
      } else if (typeof result.emailSenderPhone === "object" && !__.isEmpty(result.emailSenderPhone)) {
        const  phoneNumbers = Object.keys(result.emailSenderPhone);
        let mobile = "";
        let telephone = "";
        if (phoneNumbers.length > 1) {
          phoneNumbers.forEach(type => {
            if (["mobile", "mob", "m", "cell", "cellular", "cellphone", "handy"].includes(type.toLowerCase()))
              mobile = result.emailSenderPhone[type].replaceAll(/[\s()-]/g, "");
            if (["telephone", "phone", "p", "work", "workphone"].includes(type.toLowerCase()))
              telephone = result.emailSenderPhone[type].replaceAll(/[\s()-]/g, "");
          });
        }
        result.emailSenderPhone = mobile !== "" ? mobile : telephone;
      }
    }

    const contactNameParts = [];
    if (rowData.forename)
      contactNameParts.push(rowData.forename);
    if (rowData.initials)
      contactNameParts.push(rowData.initials);
    if (rowData.surname)
      contactNameParts.push(rowData.surname);
    const contactName = contactNameParts.length > 0 ? contactNameParts.join(" ") : null;

    let enquirySenderEmail = null;
    if (result?.emailSenderEmail && validateEmail(result?.emailSenderEmail)) {
      enquirySenderEmail = result?.emailSenderEmail;
    } else if (result?.emailSenderAddress && validateEmail(result?.emailSenderAddress)) {
      enquirySenderEmail = result?.emailSenderAddress;
    } else {
      enquirySenderEmail = rowData.sender;
    }

    const newEnquiryTemplate = {
      enquiryUuid: uuidv4(),
      enquirySource: "EMAIL",
      sourceEmailUuid: rowData.emailMessageUuid,
      enquiryDate: moment.utc().toISOString(),
      enquiryInfo: cargo.length > 0 && cargo[0].notes ? cargo[0].notes : null,
      companyName: result?.emailSenderCompanyName || rowData.companyName,
      companyUuid: result?.companyUuid || rowData.companyUuid,
      companyContact: result?.companyContactUuid || contactName,
      contactName: result?.emailSenderName || contactName,
      contactEmail: enquirySenderEmail,
      contactPhone: result?.emailSenderPhone,
      originAirportCode: cargo.length > 0 && !__.isEmpty(cargo[0]) ? cargo[0].originAirportCode : "",
      destinationAirportCode: cargo.length > 0 && !__.isEmpty(cargo[0]) ? cargo[0].destinationAirportCode : "",
      payload: cargo.length > 0 && !__.isEmpty(cargo[0]) ? cargo[0].payload : undefined,
      payloadUom: "",
      cargoAvailability: enquiryDate && enquiryDate.isValid() ? enquiryDate.format("YYYY-MM-DD") : null,
      requiredAtDestination: null,
      orderFirmness: 0,
      accountManagerId: null,
      status: "NEW",
      nextActionDate: nextActionDateTime().format("YYYY-DD-MM HH:mm:ss"),
      cargo: cargo,
      aiComments: result?.aiComments ? result.aiComments : null
    }

    return newEnquiryTemplate;
  }
  const createNewEnquiryUsingAi = (rowData) => {
    if (rowData.extractedContent && !__.isEmpty(rowData.extractedContent)) {
      const newEnquiryTemplate = processEmailAnalysisResult(rowData.extractedContent, rowData);
      eventBus.trigger("AddEnquiryDialog.open", {...newEnquiryTemplate});
    } else {
      analyseEmail(SystemData, rowData.emailBody, rowData.sender).then(result => {
        debugger;
        const newEnquiryTemplate = processEmailAnalysisResult(result, rowData);
        eventBus.trigger("AddEnquiryDialog.open", {...newEnquiryTemplate});
      }).catch(error => {
        SystemData.openAlert("Processing Error", "Could not process email content. Enter values manually");
      });
    }

    // eventBus.trigger("AddEnquiryDialog.open", {...newEnquiryTemplate})
  }

  const createNewEnquiry = (rowData) => {
    const valuesForEnquiry = {
      commodity: "",
      dimensions: "",
      enquiryDate: undefined,
      emailSenderCompanyName: "",
      emailSenderName: "",
      emailSenderPhone: "",

    }
    const newEnquiryTemplate = processEmailAnalysisResult(valuesForEnquiry, rowData);
    eventBus.trigger("AddEnquiryDialog.open", {...newEnquiryTemplate});
  }

  const updateMessageRecord = (rowData, updateFlag) => {
    switch (updateFlag) {
      case "spam":
        rowData["isSpam"] = true;
        break;
      case "ignore":
        rowData["isIgnored"] = true;
        break;
      case "snoozeHour":
        rowData["snoozeUntil"] = moment.utc().add(1, "hour").toISOString();
        break;
      case "snoozeDay":
        rowData["snoozeUntil"] = moment.utc().add(1, "day").toISOString();
        break;
      case "snoozeTwoDays":
        rowData["snoozeUntil"] = moment.utc().add(2, "days").toISOString();
        break;
      case "snoozeWeek":
        rowData["snoozeUntil"] = moment.utc().add(1, "week").toISOString();
        break;
    }
    new Promise((resolve, reject) => {
      SystemData.toggleLoadingProgress(true,"Applying actions...");
      handleUpdateMessage.mutate({record: rowData, oldRecord: rowData, resolve, reject, extraKeys});
    }).then(()=>{queryClient.invalidateQueries(["EmailMessages"]); SystemData.toggleLoadingProgress(false,"");})
      .catch((error) => {SystemData.toggleLoadingProgress(false,"");; console.log("it's bad") /* TODO: create an alert */});
  }

  const snoozeOptions = [
    {action: "snoozeHour", itemLabel:"For and Hour" ,itemIcon: (<CalendarToday/>)},
    {action: "snoozeDay", itemLabel:"For a Day" ,itemIcon: (<Today/>)},
    {action: "snoozeTwoDays", itemLabel:"For Two Days" ,itemIcon: (<InsertInvitation/>)},
    {action: "snoozeWeek", itemLabel:"for a Week" ,itemIcon: (<DateRange/>)},
  ];

  const rowActionOptions = [
    {action: "Convert to Enquiry Using AI", itemLabel: "Convert to Enquiry Using AI", itemIcon: <OpenAiIcon/>, historySuffix: "", statuses: ["ALL"], multiSelection: false},
    {action: "Convert to Enquiry", itemLabel: "Convert to Enquiry", itemIcon: <FiberNew/>, historySuffix: "", statuses: ["ALL"], multiSelection: false},
    {action: "Mark as Spam", itemLabel: "Mark as Spam", itemIcon: <UnsubscribeOutlined/>, historySuffix: "", statuses: ["ALL"], multiSelection: true},
    {action: "Ignore", itemLabel: "Ignore", itemIcon: <DoNotDisturb/>, historySuffix: "", statuses: ["ALL"], multiSelection: true},
    {action: "Snooze", itemLabel: "Snooze", itemIcon: <Snooze/>, historySuffix: "", statuses: ["ALL"], itemMenu: snoozeOptions, multiSelection: true},
  ];

  async function executeRowAction(params) {
    // console.log(selectedRowsData.current)

    if (params) {
      let recordsToAction = [];
      if (selectedRowsData?.current?.length >= 1) {
        recordsToAction = [...selectedRowsData?.current]
      } else {
        recordsToAction.push(params.rowData)
      }

      for (const rowData of recordsToAction) {
        let messageContent = null;
        switch (params.selectedAction) {
          case "Convert to Enquiry Using AI":
            messageContent = await getEmailMessageContent(rowData.emailMessageUuid);
            createNewEnquiryUsingAi({...rowData, textBody: messageContent.textBody, emailBody: messageContent.emailBody, attachments: messageContent.attachments, sender: messageContent.sender});
            break;
          case "Convert to Enquiry":
            messageContent = await getEmailMessageContent(rowData.emailMessageUuid);
            createNewEnquiry({...rowData, textBody: messageContent.textBody, emailBody: messageContent.emailBody, attachments: messageContent.attachments});
            break;
          case "Mark as Spam":
            updateMessageRecord(rowData, "spam");
            break;
          case "Ignore":
            updateMessageRecord(rowData, "ignore");
            break;
          case "snoozeHour":
          case "snoozeDay":
          case "snoozeTwoDays":
          case "snoozeWeek":
            updateMessageRecord(rowData, params.selectedAction);
            break;
          default:
            break;
        }
      }
    }
  }

  const getEmailMessageContent = async (emailMessageUuid) => {
    const { data } = await axios.get(
      `${apiSitePrefix}/emailmessages/list/${emailMessageUuid}`
    );
    if (data && Array.isArray(data) && data.length > 0) {
      return data[0];
    }
    return {textBody: "", emailBody: "", attachments:""}
  }

  const handleRowClick = async (event, rowData) => {
    // if (rowData.tableData.id >= 0 && tableRef?.current?.dataManager?.selectedCount < 2) {
    if (rowData.tableData.id >= 0) {
      const messageContent = await getEmailMessageContent(rowData.emailMessageUuid);
      selectedRow = rowData.tableData.id;
      if (eventBus) {
        if (__.has(rowData, "attachments") && JSON.stringify(rowData.attachments).length > 2) {
          rowData ["hasAttachments"] = true;
        }
        if (props.onChangeSelected && typeof props.onChangeSelected === "function") {
          props.onChangeSelected({...rowData, textBody: messageContent.textBody, emailBody: messageContent.emailBody, attachments: messageContent.attachments});
        }

        eventBus.trigger('MessageManagementList.rowSelected', {...rowData, textBody: messageContent.textBody, emailBody: messageContent.emailBody, attachments: messageContent.attachments});
      }
    }
  }

  const renderActionIcon = (rowData) => {
    return (
      <Help style={{width:24, height:24 }} color={(state.canEdit && tableRef?.current?.dataManager?.selectedCount < 2 ? (rowData.tableData.id === selectedRow ? 'secondary' : 'primary') : 'disabled')}/>
    );
  }

  const getColumns = (tableWidth) => {
    const baseColumn = {
      cellStyle: { fontSize:11, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      filterCellStyle: { padding: "4px 2px" },
      editable: "never",
      sorting: false,
      resizable: false,
    };
    const borderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #f5f5f5" };
    const invisibleBorderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const boldInvisibleBorderedCellStyle = { fontSize: 13, fontWeight: 800, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const borderedHeaderStyle = { borderLeft: "0px solid rgb(63, 63, 63)", borderRight: "1px solid rgb(63, 63, 63)" };
    const borderedFilterStyle = {}; //{ borderLeft: "0px solid #ffffff", borderRight: "1px solid #ffffff" };

    return [
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        ...ColumnWidth(100, 1, undefined, {...borderedCellStyle, borderLeft: "1px solid #f5f5f5"}, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Message Date",
        field: "receivedAt",
        filterComponent: FullWidthFilter,
        render: rowData => {
          if (rowData?.tableData?.isScrolling) return (
            <span>{moment(state.direction === "IN" ? rowData.receivedAt : rowData.sentAt).format("DD/MM/YYYY HH:mm")}</span>
          );
          return (<span>{moment(state.direction === "IN" ? rowData.receivedAt : rowData.sentAt).format("DD/MM/YYYY HH:mm")}</span>)
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, 1, undefined, borderedCellStyle, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Sender",
        field: "sender",
        filtering: true,
        filterComponent: FullWidthFilter,
        render: rowData => {
          if (rowData?.tableData?.isScrolling) return (
            <LightTooltip title={<span style={{whiteSpace: "pre-wrap"}}>`Sender: ${rowData.sender}
              To: ${rowData.toAddress}
              CC: ${rowData.ccAddress}`</span>}>
              <div style={{width:120, maxWidth:120, textOverflow: "ellipsis", whiteSpace:"nowrap", overflow: "hidden"}}>
                {rowData.sender}
              </div>
            </LightTooltip>
          );
          return (
            <LightTooltip title={<AddressToolTip sender={rowData.sender} toAddress={rowData.toAddress} ccAddress={rowData.ccAddress}/>} style={{width:"maxContent", maxWidth:"700px"}}>
              <div style={{width:120, maxWidth:120, textOverflow: "ellipsis", whiteSpace:"nowrap", overflow: "hidden"}}>
                {rowData.sender}
              </div>
            </LightTooltip>
          )
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(350, 1, undefined, borderedCellStyle, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Subject",
        field: "subject",
        filtering: true,
        filterComponent: FullWidthFilter,
        render: rowData => {
          if (rowData?.tableData?.isScrolling) return (
            <LightTooltip title={rowData.subject}>
              <div style={{width:350, maxWidth:350, textOverflow: "ellipsis", whiteSpace:"nowrap", overflow: "hidden"}}>
                {rowData.subject}
              </div>
            </LightTooltip>
          );
          return (
            <LightTooltip title={rowData.subject}>
              <div style={{width:250, maxWidth:250, textOverflow: "ellipsis", whiteSpace:"nowrap", overflow: "hidden"}}>
                {rowData.subject}
              </div>
            </LightTooltip>
          )
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(80, 1, undefined, borderedCellStyle, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Status",
        field: "processStatus",
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(180, 1, undefined, borderedCellStyle, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Error",
        field: "error",
        filterComponent: FullWidthFilter,
        render: rowData => {
          if (rowData?.tableData?.isScrolling)
            return (
              <LightTooltip title={rowData.error}>
                <div style={{width:180, maxWidth:180, textOverflow: "ellipsis", whiteSpace:"nowrap", overflow: "hidden"}}>
                  {rowData.error}
                </div>
              </LightTooltip>
          );
          return (
            <LightTooltip title={rowData.error}>
              <div style={{width:180, maxWidth:180, textOverflow: "ellipsis", whiteSpace:"nowrap", overflow: "hidden"}}>
                {rowData.error}
              </div>
            </LightTooltip>
          );
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(44),
        title: "Action",
        field: "bookingUuid",
        render: (rowData)=> {
          return(
            <IconButtonMenu
              style={{ color: theme.palette.primary.main }}
              hidden={false}
              disabled={!state.canEdit || tableRef?.current?.dataManager?.selectedCount > 1}
              icon={renderActionIcon(rowData)}
              size={"small"}
              offset={-1}
              tooltip={"Enquiry Email Actions"}
              menuItems={tableRef?.current?.dataManager?.selectedCount > 1 ? __.filter(rowActionOptions, {multiSelection: true}): rowActionOptions}
              callback={executeRowAction}
              callbackParams={{rowData: rowData}}
            />
          )
        },
        filterComponent: (props)=> {
          return (
            <IconButtonMenu
              style={{ color: theme.palette.primary.main }}
              hidden={false}
              disabled={!state.canEdit || tableRef?.current?.dataManager?.selectedCount < 2}
              icon={<Help style={{width:24, height:24 }} color={(!state.canEdit || tableRef?.current?.dataManager?.selectedCount < 2 ? 'disabled' : 'primary')}/>}
              size={"small"}
              offset={-1}
              tooltip={"Enquiry Email Actions"}
              menuItems={tableRef?.current?.dataManager?.selectedCount > 1 ? __.filter(rowActionOptions, {multiSelection: true}): rowActionOptions}
              callback={executeRowAction}
              callbackParams={{rowData: null}}
            />
          )
        }
      }
    ];
  };

  const [tableContainerRef, { width: tableWidth, height: tableHeight}] = useDimensions({});

  const selectedRowsData = createRef();

  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight:46;
  const bodyHeight = tableHeight - thead;


  const idleTimer = useRef(null);
  let interval = null;
  const onActive = () => {
    console.log('active');
    if (interval) {
      clearInterval(interval);
    }
  }
  const onIdle = () => {
    console.log('inactive');
    interval = setInterval(()=> {
      console.log('invalidating', extraKeys);
      queryClient.invalidateQueries(['Bookings',...extraKeys]);
    },60*1000);
  }

  useEffect(()=> {
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 5, position: "relative"}}>
      <div style={{background: "#ffffff", paddingTop: 16, paddingLeft: 20, paddingRight: 4, nowidth: "calc(100% - 24px)"}}>
        <div ref={tableContainerRef} className={state.canEdit?classes.bookingQueueListEdit:classes.bookingQueueList} style={{width: "100%", minWidth: "100%", height: `calc(100vh - ${props.height + 16}px)`}}>
        <MaterialTable
          icons={{ Filter: React.forwardRef((props, ref) => <Icon className="filterIcon">filter_list</Icon> )}}
          tableRef={tableRef}
          style={{
            border: "none", //"1px solid #aaaaaa",
            boxShadow: "none",
            borderRadius: 0,
          }}
          onRowClick={handleRowClick}
          onSelectionChange={(selectedRows, currentRow)=> {
            selectedRowsData.current = selectedRows;
            // if (selectedRows.length > 1)
            //   eventBus.trigger('MessageManagementList.rowSelected', null);
            // else if (selectedRows.length ===1)
            //   eventBus.trigger('MessageManagementList.rowSelected', selectedRows[0]);
            if (selectedRows.length ===1)
              eventBus.trigger('MessageManagementList.rowSelected', selectedRows[0]);
          }}
          options={{
            highlightRowStyle: () => {
              const selectedRowCount = selectedRowsData?.current?.length;
              if (selectedRowCount === undefined || selectedRowCount < 2)
                return {backgroundColor: "#F69259"}
              return {backgroundColor: "#F2F2F2"}
            },
            actionsColumnIndex: -1,
            cellStyle: {
              padding: "4px 2px",
            },
            filterCellStyle: {
              padding: "4px 2px",
            },
            headerStyle: {
              backgroundColor: theme.palette.tableHeader.backgroundColor,
              color: theme.palette.tableHeader.textColor,
              padding: 2,
            },
            filterRowStyle: {
              paddingLeft: state.canEdit ? 30 : 0,
              width: state.canEdit ? "calc(100% - 45px)" : "100%",
            },
            toolbar: false,
            toolbarButtonAlignment: "right",
            detailPanelType: "single",
            padding: 'dense',
            paging: false,
            filtering: true,
            search: false,
            searchFieldAlignment: "left",
            columnResizable: false,
            selection: state.canEdit,
            // selectionProps: rowData => {
            //   const confirmOption = __.find(rowActionOptions, {action: "Confirm Booking"});
            //   const selectionAllowed =  confirmOption && confirmOption.statuses.includes(rowData.bookingStatus) ;
            //   return ({ disabled: !selectionAllowed })
            // },
            // // disableSelection: rowData => {
            // //   const confirmOption = __.find(rowActionOptions, {action: "Confirm Booking"});
            // //   const selectionAllowed =  confirmOption && confirmOption.statuses.includes(rowData.bookingStatus);
            // //   return !selectionAllowed;
            // // }
          }}
          components={{
            ...defaultComponentsScroll,
            Body: (props) => (
              <MaterialTableBody
                {...props}
                rowHeight={46}
                overscanRowCount={10}
                addVirtualScrolling={true}
                bodyHeight={bodyHeight}
                headerHeight={thead}
                filterRowHeight={46}
              />
            ),
            SummaryRow: MaterialTableSummaryRow
          }}
          localization={{
            header: {
              actions: 'Action'
            },
          }}
          title="Email Messages"
          columns={getColumns(tableWidth)}
          data={emailMessages.data}
        />
        </div>
      </div>
      <AddEnquiryDialog
        systemData={SystemData}
        defaults={defaults}
        record={{}}
        canEdit={state.canEdit}
        newEnquiry={true}
        formMode={"OPERATOR"}
        close={handleCloseAddEnquiry}
        classes={classes}
        dialogStates={{showDialog: false, title: "Create New Enquiry from Email Message", dialogMessage: "", record: {}}}
        showFullPage={false}
        fullPageStyle={{backgroundColor: "#e1e1e1"}}
        handleAdd={handleAddEnquiry}
      />
    </div>
  );

}

export default EnquiryMessageList;
